/**
 * Cart Operation Queue Manager
 * Serializes cart operations to prevent race conditions and overlapping requests
 */
class CartOperationQueue {
  /** @type {Array<{operation: Function, resolve: Function, reject: Function, priority: number}>} */
  #queue = [];

  /** @type {boolean} */
  #processing = false;

  /** @type {Promise<any> | null} */
  #currentOperation = null;

  /** @type {number} */
  #operationCount = 0;

  /**
   * Monotonic sequence of the operation currently executing. Because operations run
   * strictly one at a time (serial queue), this is also their completion order. A queued
   * mutation passes this to `primeCartCache(cart, sequence)` so an out-of-order prime
   * (a response that resolves after a later write already primed) is rejected.
   * @type {number}
   */
  #currentSequence = 0;

  /**
   * Enqueues an operation to be executed when queue is available
   * @param {Function} operation - Async function to execute
   * @param {Object} [options] - Operation options
   * @param {number} [options.priority=0] - Higher priority operations execute first
   * @param {string} [options.id] - Optional ID for operation deduplication
   * @returns {Promise<any>}
   */
  async enqueue(operation, options = {}) {
    const { priority = 0, id } = options;

    return new Promise((resolve, reject) => {
      // If ID provided and operation already queued, skip
      if (id && this.#queue.some(item => item.id === id)) {
        console.log(`Skipping duplicate operation: ${id}`);
        resolve(null);
        return;
      }

      this.#queue.push({ 
        operation, 
        resolve, 
        reject, 
        priority,
        id,
        timestamp: Date.now()
      });

      // Sort by priority (descending) then timestamp (ascending)
      this.#queue.sort((a, b) => {
        if (b.priority !== a.priority) {
          return b.priority - a.priority;
        }
        return a.timestamp - b.timestamp;
      });

      this.#processQueue();
    });
  }

  /**
   * Processes the next operation in the queue
   */
  async #processQueue() {
    if (this.#processing || this.#queue.length === 0) {
      return;
    }

    this.#processing = true;
    const { operation, resolve, reject, id } = this.#queue.shift();

    try {
      this.#operationCount++;
      const sequence = this.#operationCount;
      this.#currentSequence = sequence;
      console.log(`[Queue] Starting operation ${sequence}${id ? ` (${id})` : ''}, ${this.#queue.length} remaining`);

      // Pass the operation its monotonic sequence so it can prime cart-state in
      // queue-completion order. Pre-existing zero-arg operations ignore it.
      this.#currentOperation = operation(sequence);
      const result = await this.#currentOperation;
      
      console.log(`[Queue] Completed operation ${sequence}`);
      resolve(result);
    } catch (error) {
      // Don't log AbortError as these are intentional
      if (error.name !== 'AbortError') {
        console.error('[Queue] Operation failed:', error);
      }
      reject(error);
    } finally {
      this.#currentOperation = null;
      this.#processing = false;
      
      // Process next operation if available
      if (this.#queue.length > 0) {
        // Small delay to allow UI to update
        await new Promise(resolve => setTimeout(resolve, 10));
        this.#processQueue();
      }
    }
  }

  /**
   * Clears all pending operations
   * @param {boolean} [rejectPending=true] - Whether to reject pending operations
   */
  clear(rejectPending = true) {
    if (rejectPending) {
      const error = new Error('Queue cleared');
      error.name = 'QueueClearedError';
      
      while (this.#queue.length > 0) {
        const { reject } = this.#queue.shift();
        reject(error);
      }
    } else {
      this.#queue = [];
    }
    
    console.log('[Queue] Cleared all pending operations');
  }

  /**
   * Gets the current queue length
   * @returns {number}
   */
  get length() {
    return this.#queue.length;
  }

  /**
   * Checks if queue is currently processing
   * @returns {boolean}
   */
  get isProcessing() {
    return this.#processing;
  }

  /**
   * Gets current operation promise (for testing/debugging)
   * @returns {Promise<any> | null}
   */
  get currentOperation() {
    return this.#currentOperation;
  }

  /**
   * Monotonic sequence of the operation currently executing — the prime-ordering token
   * a queued mutation hands to `primeCartCache(cart, sequence)`.
   * @returns {number}
   */
  get currentSequence() {
    return this.#currentSequence;
  }
}

// Export singleton instance
export const cartOperationQueue = new CartOperationQueue();
